<script lang="ts">
	import { beforeUpdate } from 'svelte';
	import { devices } from 'src/stores/lists/devices';
import { os } from 'src/stores/session/native-bridge';

	import { postReq, localReq } from '../../../services/http';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';

	import { get_local_ip, real_id } from '../../../helpers/interface_helpers';
	import { gen1CloudReq } from 'src/services/gen_1_reqs';

	import { format } from 'svelte-i18n';

	import { Slider, toast } from '@Shelly/shelly-design-system';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	import PinUnlock from '../../../components/PinUnlock.svelte';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';

	export let device, status;
	export let settings = null;

	let value = 0;
	let valueCache = 0;
	let ison = false;
	let localIp = false;

	let pinLocked;
	let showModal = false;

	let callbackValue;

	let isLoading = false;

	let marks = [];

	// TODO For G1 device there are predefined 4 slots for favorite positions.
	// If they are not set, by default value will be 0.
	// Discuss what to do if user want to set 0 value as favorite.
	// For now filter marks with value different from 0.
	// For gen2 devices will leave 0 as valid favorite, because inside local web it is valid favorite

	let sliderFilteredMarks;
	let mappedMarks;

	$: haveMarks = settings?.favorites_enabled;

	$: if (
		device.category !== 'sensor' &&
		typeof status[device.category.split('s_')[0] + 's'] !== 'undefined'
	) {
		value = status[device.category.split('s_')[0] + 's'][device.channel].current_pos;
	}

	beforeUpdate(() => {
		localIp = get_local_ip(device.id);
		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;
		marks =
			settings?.favorites?.map((f) => {
				return {
					value: f.pos,
				};
			}) || [];

		sliderFilteredMarks = marks.filter((m) => m.value !== 0);
		mappedMarks = marks
			.filter((m) => m.value !== 0)
			.map((m, index): { value: number; dbIndex: number } => {
				return {
					...m,
					dbIndex: index,
				};
			});
	});

	const changePosition = async (selectedPosition) => {
		if (!pinLocked) {
			valueCache = value;
			value = selectedPosition;
			isLoading = true;
			if ($os === 'IOS') {
				hapticFeedback('buttonPressed');
			}
			let res;
			if (localIp) {
				const url = `${localIp}/roller/${device.channel}`;

				const data = { go: 'to_pos', roller_pos: value };

				res = await localReq(device, url, 'post', data, 6000, false);
			} else {
				res = await gen1CloudReq(device, 'roller_to_pos', {
					pos: value,
				});
			}

			if (res.isok) {
				const _status = status;

				_status[device.category.split('s_')[0] + 's'][device.channel].current_pos = value;

				devices_status.update_device_status({
					id: real_id(device.id),
					msg: _status,
				});
			} else {
				value = valueCache;
				toast.open({
					type: 'danger',
					title: $format('device_control_failed_long'),
				});
			}
			isLoading = false;
		} else {
			callbackValue = selectedPosition;
			showModal = true;
		}
	};

	const handleMarkChange = async (value: number, type: 'add' | 'delete' = 'add') => {
		let res;
		isLoading = true;

		const isTypeAdd = type === 'add';
		const markIndex = isTypeAdd ? settings.favorites.findIndex((f) => f.pos === 0) : value;
		if (markIndex === -1) {
			isLoading = false;
			toast.open({
				type: 'danger',
				title: 'Unable to save!',
				text: 'All favorite positions are set. Delete an existing to add a new one.',
				isTitleBold: true,
			});
			return;
		}

		if (isTypeAdd) {
			marks[markIndex] = { value: value };
		} else {
			marks = marks.filter((_, index) => index !== markIndex);
		}

		let favorite: { name?; pos? } = Object.values(settings.favorites)[markIndex];
		favorite.pos = isTypeAdd ? value : 0;

		if (!favorite.name) {
			favorite.name = '';
		}

		res = await postReq('device/generic_command', {
			id: device.id,
			command: 'favorites',
			payload: JSON.stringify({ id: markIndex, ...favorite }),
		});
		if (res.isok) {
			settings.favorites[markIndex] = favorite;
			devices_settings.update_devices_settings(device.id, settings);
		}
		onSettingsResponse(res, device, {
			success: isTypeAdd
				? $format('favorite_save_scc_toast', {
						values: { favorite_name: favorite.name },
					}) || `${favorite.name} is saved.`
				: `Favorite position (${favorite.name}) deleted.`,
		});
		isLoading = false;
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		changePosition(callbackValue);
		callbackValue = undefined;
	};

	const handleClose = () => {
		value = valueCache;
	};
	const handleDelete = async (e: CustomEvent) => {
		if (!pinLocked) {
			const deleteMarkData: { value?: number; dbIndex?: number } = Object.values(
				mappedMarks,
			).filter((m: { dbIndex: number; value: number }) => m.dbIndex === e.detail.index)[0];

			Object.values(marks).forEach((m, i) => {
				if (m.value === deleteMarkData.value) {
					deleteMarkData.dbIndex = i;
				}
			});
			await handleMarkChange(deleteMarkData.dbIndex, 'delete');
		} else {
			showModal = true;
		}
	};
	const a11yProps = {
		sliderAriaLabel: $format('label_set_position', { default: 'Set position' }),
		ariaLabel: $format('label_set_position', { default: 'Set position' }),
		ariaLabelValue: $format('label_set_position', { default: 'Set position' }),
	};
</script>

<!-- stopSaveDeleteMarks={isSharedDevice} -->
<Slider
	id="slider-195o12y"
	{a11yProps}
	class="flex-full-height mb-l"
	title={$format('state_opened', { default: 'Opened' })}
	stopSaveDeleteMarks={device.shared}
	{value}
	marks={sliderFilteredMarks}
	{haveMarks}
	on:value_change={(e) => {
		changePosition(e.detail.value);
	}}
	on:move={() => {
		if ($os === 'IOS') {
			hapticFeedback('selection');
		}
	}}
	{isLoading}
	on:new_mark={async (e) => {
		!device.shared && (await handleMarkChange(e.detail.value));
	}}
	on:delete_mark={!device.shared && handleDelete}
/>

{#if showModal}
	<PinUnlock
		bind:showModal
		on:unlock={handlePin}
		on:close={handleClose}
		on:cancel={() => (showModal = false)}
	/>
{/if}
